import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import { Search, ShoppingCart, Heart, User, MapPin, Menu, X, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, inr } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SERVICE_NAV = [
  { to: "/services", label: "Landscaping" },
  { to: "/bulk", label: "Bulk Orders" },
  { to: "/site-visit", label: "Nursery Visit" },
];

// How many category links show inline before the rest collapse into the "More" dropdown.
const VISIBLE_CATEGORY_COUNT = 3;

export default function Header() {
  const nav = useNavigate();
  const location = useLocation();
  const { count } = useCart();
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [pin, setPin] = useState(localStorage.getItem("yp_pin") || "");
  const [locating, setLocating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showMobile, setShowMobile] = useState(false);
  const [categoryNav, setCategoryNav] = useState([]);
  const [showCategoryStrip, setShowCategoryStrip] = useState(true);
  const timer = useRef();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await api.categories();
        if (cancelled) return;
        setCategoryNav((cats || []).map((c) => ({ to: `/shop/${c.id}`, label: c.name })));
      } catch { }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hides the category strip once the page has scrolled well past it, so it doesn't sit under
  // the sticky header. A single fixed threshold (rather than comparing consecutive scroll
  // deltas) means the strip's visibility is a pure function of scroll position — it can't flip
  // back and forth from noisy, direction-inconsistent scroll events (e.g. trackpad momentum).
  useEffect(() => {
    const HIDE_AT = 120;
    let ticking = false;

    const update = () => {
      setShowCategoryStrip(window.scrollY < HIDE_AT);
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const visibleCategories = useMemo(
    () => categoryNav.slice(0, VISIBLE_CATEGORY_COUNT),
    [categoryNav]
  );
  const hiddenCategories = useMemo(
    () => categoryNav.slice(VISIBLE_CATEGORY_COUNT),
    [categoryNav]
  );
  const NAV = [...visibleCategories, ...SERVICE_NAV];
  const MOBILE_NAV = [...categoryNav, ...SERVICE_NAV];

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q || q.length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const data = await api.search.suggest(q);
        setSuggestions(data.items || []);
      } catch { }
    }, 200);
  }, [q]);

  // Keeps the search box showing what the URL says when landing directly on a search-results
  // link (e.g. a shared `/shop?q=...` URL) — without this the box stayed empty even though the
  // page below it was already filtered.
  useEffect(() => {
    if (location.pathname === "/shop") {
      setQ(new URLSearchParams(location.search).get("q") || "");
    }
  }, [location.pathname, location.search]);

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    setQ(trimmed);
    nav(`/shop?q=${encodeURIComponent(trimmed)}`);
    setSuggestions([]);
  };

  const savePin = () => {
    if (pin.length === 6) {
      localStorage.setItem("yp_pin", pin);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location isn't supported in this browser");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const { pincode } = await api.reverseGeocode(coords.latitude, coords.longitude);
          if (pincode) {
            setPin(pincode);
            localStorage.setItem("yp_pin", pincode);
            toast.success(`Delivery pincode set to ${pincode}`);
          } else {
            toast.error("Couldn't determine your PIN code from your location");
          }
        } catch {
          toast.error("Couldn't determine your PIN code from your location");
        } finally {
          setLocating(false);
        }
      },
      () => {
        toast.error("Location permission denied");
        setLocating(false);
      },
      { timeout: 10000 },
    );
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[var(--yp-border)]">
      {/* Top strip */}
      <div className="bg-[var(--yp-primary)] text-white text-xs">
        <div className="mx-auto max-w-7xl px-4 py-1.5 flex items-center justify-between">
          <span className="hidden sm:inline">Shipping Across India · Healthy Plant Guaranteed</span>
          <div className="flex items-center gap-4">
            <Link to="/track" data-testid="link-track" className="hover:underline">Track Order</Link>
            <span className="hidden sm:inline opacity-70">|</span>
            <Link to="/site-visit" data-testid="link-book-visit-top" className="hover:underline">Book Site Visit</Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-3 flex items-center gap-4">
        <button data-testid="mobile-menu-toggle" onClick={() => setShowMobile(true)} className="md:hidden text-[var(--yp-primary)]">
          <Menu size={24} />
        </button>
        <Link to="/" data-testid="logo-link" className="flex flex-col shrink-0">
          <div><img src="/full_logo_transparent.png" alt="YourPlants.in" className="h-10 w-auto" /></div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--yp-muted)]">Aapka Plant, Aapke Liye</div>
        </Link>

        <form onSubmit={onSubmit} className="hidden md:flex flex-1 relative">
          <div className="flex-1 flex items-center bg-[var(--yp-surface-alt)] rounded-full px-4 border border-[var(--yp-border)] focus-within:border-[var(--yp-primary)]">
            <Search size={18} className="text-[var(--yp-muted)]" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Monstera, Areca, Bougainvillea..."
              className="bg-transparent flex-1 px-3 py-2.5 outline-none text-sm"
            />
          </div>
          {suggestions.length > 0 && (
            <div data-testid="search-suggestions" className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl border border-[var(--yp-border)] shadow-lg z-50 overflow-hidden">
              {suggestions.map((s) => (
                <Link
                  key={s.id}
                  to={`/product/${s.id}`}
                  onClick={() => { setSuggestions([]); setQ(""); }}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-[var(--yp-surface-alt)]"
                >
                  <img src={s.images?.[0] || "/plant-placeholder.png"} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-xs text-[var(--yp-muted)]">{inr(s.price)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </form>

        <div className="hidden lg:flex items-center gap-2 border border-[var(--yp-border)] rounded-full px-3 py-2">
          <button
            type="button"
            data-testid="btn-use-location"
            onClick={useMyLocation}
            disabled={locating}
            aria-label="Use my current location"
            title="Use my current location"
            className="text-[var(--yp-primary)] disabled:opacity-50 shrink-0"
          >
            {locating ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
          </button>
          <input
            data-testid="pincode-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onBlur={savePin}
            placeholder="Pincode"
            className="w-16 bg-transparent outline-none text-sm"
          />
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/wishlist" data-testid="link-wishlist" className="p-2 rounded-full hover:bg-[var(--yp-surface-alt)]" aria-label="Wishlist">
            <Heart size={20} />
          </Link>
          <Link to="/cart" data-testid="link-cart" className="relative p-2 rounded-full hover:bg-[var(--yp-surface-alt)]" aria-label="Cart">
            <ShoppingCart size={20} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[var(--yp-accent)] text-[10px] font-semibold text-[var(--yp-text)] rounded-full w-5 h-5 grid place-items-center">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to={user.role === "admin" ? "/admin" : "/account"} data-testid="link-account" className="flex items-center gap-2 text-sm px-3 py-2 rounded-full hover:bg-[var(--yp-surface-alt)]">
                <User size={18} /> {user.name.split(" ")[0]}
              </Link>
              <button data-testid="btn-logout" onClick={logout} className="text-xs text-[var(--yp-muted)] hover:text-[var(--yp-primary)]">Logout</button>
            </div>
          ) : (
            <Link to="/login" data-testid="link-login" className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-[var(--yp-primary)] text-white hover:bg-[var(--yp-primary-hover)]">
              <User size={16} /> Login
            </Link>
          )}
        </div>
      </div>

      {/* Category strip */}
      <nav
        aria-hidden={!showCategoryStrip}
        className={`hidden md:grid border-t border-[var(--yp-border)] bg-white overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out ${
          showCategoryStrip ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mx-auto max-w-7xl px-4 flex items-center gap-6 overflow-x-auto">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-sm whitespace-nowrap border-b-2 ${isActive ? "border-[var(--yp-primary)] text-[var(--yp-primary)] font-semibold" : "border-transparent text-[var(--yp-text)] hover:text-[var(--yp-primary)]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {hiddenCategories.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="nav-more"
                  className="flex items-center gap-1 py-3 text-sm whitespace-nowrap border-b-2 border-transparent text-[var(--yp-primary)] font-semibold hover:underline outline-none data-[state=open]:underline"
                >
                  More <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {hiddenCategories.map((n) => (
                    <DropdownMenuItem key={n.to} asChild data-testid={`nav-more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <NavLink to={n.to}>{n.label}</NavLink>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile search */}
      <form onSubmit={onSubmit} className="md:hidden px-4 pb-3">
        <div className="flex items-center bg-[var(--yp-surface-alt)] rounded-full px-4 border border-[var(--yp-border)]">
          <Search size={18} className="text-[var(--yp-muted)]" />
          <input
            data-testid="global-search-input-mobile"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search plants..."
            className="bg-transparent flex-1 px-3 py-2.5 outline-none text-sm"
          />
        </div>
      </form>

      {/* Mobile drawer */}
      {showMobile && (
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setShowMobile(false)}>
          <div onClick={(e) => e.stopPropagation()} className="absolute left-0 top-0 h-full w-72 bg-white p-6 flex flex-col gap-4 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="font-serif-display text-xl text-[var(--yp-primary)]">Menu</div>
              <button data-testid="mobile-menu-close" onClick={() => setShowMobile(false)}><X /></button>
            </div>
            {MOBILE_NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setShowMobile(false)} className="text-sm py-2 border-b border-[var(--yp-border)]">
                {n.label}
              </Link>
            ))}
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setShowMobile(false)} className="mt-4 btn-primary text-center py-2 rounded-full text-sm">My Account</Link>
            ) : (
              <Link to="/login" onClick={() => setShowMobile(false)} className="mt-4 btn-primary text-center py-2 rounded-full text-sm">Login / Register</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
